import {Component, ComponentWillLoad, Event, EventEmitter, h, Listen, Prop, State, Watch} from '@stencil/core';
import {Dayjs} from "dayjs";

import {NjConnectionData} from "../../global/JourneyStore";
import {asLocalTime, atStartOfDay, formatDate, formatDuration, formatIsoDate, formatTime} from '../../utils/date';
import {FromToData} from '../nj-from-to/nj-from-to';
import {NjLoaderSize} from '../nj-loader/nj-loader-size';
import {i18n} from '../../utils/I18n';
import {DATA_STORE_KEY_FROM_TO_RETURN, dataStore} from '../../global/DataStore';
import {ReservationMode} from '../nj-reservation/ReservationMode';
import {PassangerType} from '../nj-passangers/PassangerType';
import {ConnectionWithOffersV6} from '../../api/Ticketshop';
import {getPassangersFromStore, removeBikes, setPassangersIntoStore} from '../nj-passangers/passanger-utils';
import {trackMatomoEvent} from "../../utils/matomo";
import {RestService} from "../../services/rest.service";
import {ConnectionDto} from "../../http/nj-booking";
import {TrainType} from "./TrainType";

@Component({
  tag: 'nj-connections',
  styleUrl: 'nj-connections.scss',
  shadow: false,
})
export class NjConnections implements ComponentWillLoad {

    private firstView: boolean = true;
    private outwardConnectionsWithOffers: ConnectionWithOffersV6[] = [];
    private returnConnectionsWithOffers: ConnectionWithOffersV6[] = [];
    private hasPassengersWithBikes: boolean;

    private connectionsOutward: ConnectionDto[];
    private connectionsReturn: ConnectionDto[];

    @State()
    private showWarningBikes: boolean = false;

    @State()
    private showReturnForm: boolean = false;

    @State()
    private connectionDataOutward: NjConnectionData[];
    @State()
    private connectionDataReturn: NjConnectionData[];

    @State()
    private loadOutwardDataMode: number;
    @State()
    private loadReturnDataMode: number;

    @State()
    private outwardDateWarning: string = null;
    @State()
    private returnDateWarning: string = null;

    @State()
    private outwardBackEnabled: boolean = true;
    @State()
    private returnBackEnabled: boolean = true;

    @Prop({mutable: true})
    fromToReturnData: FromToData;

    @Prop({mutable: true})
    fromToData: FromToData;

    @Prop()
    parentShadowRoot: ShadowRoot;

    @State() linkWarning_temporarilyUnavailable?: string;
    @State() linkWarning_noAvailability?: string;
    @State() linkWarning_temporarilyInsufficientAvailability?: string;

    @State() enableNewInfobox?: boolean;
    @State() enableSearchagent?: boolean;
    @State() simulateWarnings?: boolean;

  @Watch('fromToData')
  async fromToDataPropHandler() {
      await this.loadData(false, this.showReturnForm);
  }

  @Watch('fromToReturnData')
  async fromToReturnDataPropHandler(newValue: FromToData) {
      this.showReturnForm = !!newValue;
      await this.loadData(true, false);
  }

  @Event() njReturnFormActivated: EventEmitter<void>;
  @Event() njReturnFormDeactivated: EventEmitter<void>;
  @Event() njPassengersChanged: EventEmitter<void>;

  private firstPossibleReturnDate?: Dayjs;

  componentWillLoad() {
      // Check if we should be showing the return form
      this.showReturnForm = !!this.fromToReturnData;
      this.loadData();

      const rootElement = document.querySelector('nj-root' || 'nj-root-search');
      if (rootElement) {
        this.linkWarning_noAvailability = rootElement.getAttribute('data-link-warning_no-availability')
        this.linkWarning_temporarilyUnavailable = rootElement.getAttribute('data-link-warning_temporarily-unavailable')
        this.linkWarning_temporarilyInsufficientAvailability = rootElement.getAttribute('data-link-warning_temporarily-insufficient-availability')
        this.enableNewInfobox = rootElement.getAttribute('data-enable-new-infobox') == "true";
        this.enableSearchagent = rootElement.getAttribute('data-enable-searchagent') == "true";
        this.simulateWarnings = rootElement.getAttribute('data-simulate-warning') == "true";
      }
  }

  private async loadData(omitOutward?: boolean, omitReturn?: boolean) {
      if (!this.fromToData) {
          return;
      }

      if (!omitOutward) {
          await this.loadOutwardData();
          this.firstPossibleReturnDate = this.calculateFirstPossibleReturnDate();
      }
      if (!omitReturn || this.firstPossibleReturnDate) {
          await this.loadReturnData();
      }
  }

  @Listen('passangersDialogClosed', {target: 'window'})
  passagnersDialogClosedHandler(event: CustomEvent) {
      // Passengers were changed in the dialog, offers will be reloaded
      if (event.detail.name === 'passangers') {
          this.outwardConnectionsWithOffers = [];
          this.returnConnectionsWithOffers = [];
          this.showWarningBikes = false;
      }
  }

  @Listen('njOfferSearchDone')
  njOfferSearchDoneHandler(e: CustomEvent<{ mode: ReservationMode, connectionWithOffers: ConnectionWithOffersV6 }>) {
    if (e.detail.mode === ReservationMode.Outward) {
      this.outwardConnectionsWithOffers.push(e.detail.connectionWithOffers);
    } else if (e.detail.mode === ReservationMode.Return) {
      this.returnConnectionsWithOffers.push(e.detail.connectionWithOffers);
    }

    if (this.fromToData.oneWay) {
      if (this.outwardConnectionsWithOffers?.length === this.connectionDataOutward?.length) {
        // All done if it is only outward
        this.checkBikes();
      }
    } else {
      if (
        (this.outwardConnectionsWithOffers?.length === this.connectionDataOutward?.length)
        && (this.returnConnectionsWithOffers?.length === this.connectionDataReturn?.length)
      ) {
        // All done if it is outward and return
        this.checkBikes();
      }
    }
  }

  private async checkBikes() {
    const passengers = getPassangersFromStore();
    this.hasPassengersWithBikes = passengers.some(passenger => passenger.type === PassangerType.Bike);

    if (!this.hasPassengersWithBikes) {
      setTimeout(() => this.showWarningBikes = false); // Timeout to set this switch after rendering
      return;
    }

    const hasOffersForBikesOutward = this.outwardConnectionsWithOffers.some(connection => connection.offers.length > 0);
    const hasOffersForBikesReturn = this.returnConnectionsWithOffers.some(connection => connection.offers.length > 0);

    if (
      (this.fromToData.oneWay && !hasOffersForBikesOutward)
      || (!this.fromToData.oneWay && (!hasOffersForBikesOutward || !hasOffersForBikesReturn))
    ) {
      this.showWarningBikes = true;

      removeBikes(passengers);
      setPassangersIntoStore(passengers);
      this.njPassengersChanged.emit(); // Triggers update in nj-passangers and offer reload in nj-connection
    }
  }

  private async loadOutwardData(mode?: 0 | 1 | 2) {
      if (!mode) {
          this.outwardConnectionsWithOffers = [];
          this.connectionsOutward = null;
          this.connectionDataOutward = null;
          this.outwardDateWarning = null;
      }

      const searchData = {
          from: this.fromToData.from,
          to: this.fromToData.to,
          date: asLocalTime(this.fromToData.dateOutward?.context),
      }

      if (!searchData.date) {
          return;
      }

      this.loadOutwardDataMode = mode ?? 0;
      switch (mode) {
          case 0:
              break;
          case 1:
              trackMatomoEvent({ "event": "nj.connection.searchConnection.laterConnections" });
              break;
          case 2:
              trackMatomoEvent({ "event": "nj.connection.searchConnection.earlierConnections" });
              break;
      }
      this.connectionsOutward = await this.loadConnections(searchData, mode, this.connectionsOutward);
      this.connectionDataOutward = this.mapConnections(this.connectionsOutward);
      this.loadOutwardDataMode = -1;

      this.outwardBackEnabled = this.isFirstDateAfter(this.connectionDataOutward);
      this.outwardDateWarning = !mode && this.isFirstDateAfter(this.connectionDataOutward, searchData.date)
          ? i18n.getMessage("connections.warning-not-same-traveling-date")
          : null;
  }

  private async loadReturnData(mode?: 0 | 1 | 2) {
      if (!mode) {
          this.returnConnectionsWithOffers = [];
          this.connectionsReturn = null;
          this.connectionDataReturn = null;
          this.returnDateWarning = null;
      }

      const searchData = this.fromToReturnData ? {
          from: this.fromToReturnData.from,
          to: this.fromToReturnData.to,
          date: asLocalTime(this.fromToReturnData.dateReturn?.context),
      } : {
          from: this.fromToData.to,
          to: this.fromToData.from,
          date: asLocalTime(this.fromToData.dateReturn?.context),
      };

      if (!searchData.date) {
          return;
      }

      if (this.firstPossibleReturnDate && this.firstPossibleReturnDate > searchData.date) {
          // If the first possible return date is after the requested date, the search is pushed backwards
          const daysBetween = searchData.date.diff(this.fromToData.dateOutward.context, 'days');
          searchData.date = daysBetween > 1
              ? this.firstPossibleReturnDate.add(daysBetween - 1, 'days')
              : this.firstPossibleReturnDate;
      }

      this.loadReturnDataMode = mode ?? 0;
      switch (mode) {
          case 0:
              break;
          case 1:
              trackMatomoEvent({ "event": "nj.connection.searchConnection.laterConnections" });
              break;
          case 2:
              trackMatomoEvent({ "event": "nj.connection.searchConnection.earlierConnections" });
              break;
      }
      this.connectionsReturn = await this.loadConnections(searchData, mode, this.connectionsReturn);
      this.connectionDataReturn = this.mapConnections(this.connectionsReturn);
      this.loadReturnDataMode = -1;

      this.returnBackEnabled = this.isFirstDateAfter(this.connectionDataReturn);
      this.returnDateWarning = !mode && this.isFirstDateAfter(this.connectionDataReturn, searchData.date)
          ? i18n.getMessage("connections.warning-not-same-traveling-date")
          : null;
  }

  private async loadConnections(searchData: any, mode?: 0 | 1 | 2, existingConnections: ConnectionDto[] = []): Promise<ConnectionDto[]> {
      // 0 = normale Abfrage
      // 1 = Weitere Verbindungen
      // 2 = Vorherige Verbindungen

      let skip: number = null;
      const searchDate = atStartOfDay(searchData.date);

      if (!mode) {
          // Load initial connections
          existingConnections = [];
      } else if (mode === 1) {
          // Load later connections, skip existing connections after the initial search date
          skip = existingConnections.filter(conn => conn.trains[0].departure.utc >= +searchDate).length;
      } else if (mode === 2) {
          // Load earlier connections, skip existing connections before the initial search date
          skip = -existingConnections.filter(conn => conn.trains[0].departure.utc < +searchDate).length - 1;
      }

      const from = searchData.from.context.number;
      const to = searchData.to.context.number;
      const fmtDate = formatIsoDate(searchDate);

      this.firstView = false;
      try {
          const data = await RestService.instance.findConnections(from, to, fmtDate, skip);

          // Examine the text in the response
          if (!data) {
              return existingConnections;
          }

          // Combine the connections with the existing connections
          if (!mode) {
              return [...data.connections];
          } else if (mode === 1) {
              return [...existingConnections, ...data.connections];
          } else if (mode === 2) {
              return [...data.connections, ...existingConnections];
          }
      } catch (e) {
          console.warn('Looks like there was a problem. Status Code: ' + e.status);
          return existingConnections;
      }
  }

  private mapConnections(connections: ConnectionDto[]): NjConnectionData[] {
      return connections.flatMap(connection => {
          // Count number of trains numbers with same departure
          const trainNumbers = connection.trains
              .map(train => train.seatAsIC ? 2 : 1)
              .reduce((a, b) => a + b, 0);

          return connection.trains.map(train => {
              const departure = asLocalTime(train.departure.local);
              const from = {
                  ...connection.from,
                  dep_dt: train.departure.utc,
                  dep_date: formatDate(departure),
                  dep_time: formatTime(departure),
              };

              const arrival = asLocalTime(train.arrival.local);
              const to = {
                  ...connection.to,
                  arr_dt: train.arrival.utc,
                  arr_date: formatDate(arrival),
                  arr_time: formatTime(arrival),
              };

              const duration = formatDuration(train.departure.utc, train.arrival.utc);

              return {
                  train: train.train,
                  trainCount: trainNumbers,
                  departure: departure,
				  arrival: arrival,
                  from: from,
                  to: to,
                  dani: train.trainType === TrainType.DANI && (!train.daniLunaFrom || train.daniLunaFrom <= train.departure.utc),
                  seatAsIC: train.seatAsIC,
                  seatAsICText: train.seatAsICText,
                  duration_fmt: duration,
              }
          })
      });
  }

  private isFirstDateAfter(connections: NjConnectionData[], date?: Dayjs): boolean {
      if (!connections?.length) {
          return false;
      }

      return atStartOfDay(connections[0].departure) > atStartOfDay(date);
  }

  private calculateFirstPossibleReturnDate(): Dayjs | null {
      if (!this.outwardDateWarning) {
          // First outward connection on requested date, no override necessary
          return null;
      }

      return atStartOfDay(this.connectionDataOutward[0].departure).add(1, 'day');
  }

  private showReturnFormHandler() {
    this.showReturnForm = true;
    trackMatomoEvent({ "event": "nj.connection.searchConnection.alternativeReturn" });
    this.njReturnFormActivated.emit();
  }

  private hideReturnFormHandler() {
    this.showReturnForm = false;
    this.njReturnFormDeactivated.emit();
    this.fromToData.dateReturn = null;
    this.fromToReturnData = null;
    this.returnConnectionsWithOffers = [];
    dataStore().removeSessionData(DATA_STORE_KEY_FROM_TO_RETURN);
  }

  private renderLoader() {
    return (
      <p class="loader">
        <nj-loader size={NjLoaderSize.SMALL}></nj-loader>
      </p>
    );
  }

  private renderWarningDate(warningMsg?: string) {
      if (!warningMsg) return;

      return (
          <nj-validation type="WARNING">
              {warningMsg}
          </nj-validation>
      )
  }

  private renderWarningBikes() {
    if (!this.showWarningBikes) return;

    return (
      <nj-validation type="WARNING" class="validation-warning--bikes">
        <p>
          {i18n.getMessage("connections.bicycles.search-without-bicycles")}
        </p>
        <nj-support linkTarget={`/${i18n.getLang()}/angebote/fahrradmitnahme`} supportLinkTextTranslationKey="connections.bicycles.search-without-bicycles.further-information.link-text" />
      </nj-validation>
    );
  }

  private renderOutward() {
    if (!this.fromToData) return;

    return [
      <h2 id="outward-journey">{i18n.getMessage("connections-outward-journey")}</h2>,

      this.renderWarningDate(this.outwardDateWarning),

     <button class={{
      "previous": true,
      "button--connections": true,
      "invisible": !this.outwardBackEnabled
    }} type="button"  onClick={() => this.loadOutwardData(2)}><arrow-up-icon /> {i18n.getMessage("connections.previous-connections")}</button>,
      (() => {
        if (!this.connectionDataOutward && !this.firstView) {
          return this.renderLoader();
        }

        if (this.connectionDataOutward && this.connectionDataOutward.length == 0) {
          return (<nj-validation type='ERROR'>{i18n.getMessage("connections.outward-journey-no-connections")}</nj-validation>);
        }

        return [
          this.loadOutwardDataMode === 2 ? <p class="loader"><nj-loader size={NjLoaderSize.SMALL}></nj-loader> </p> : '',
          <ul>
            {this.connectionDataOutward?.map(connection => (
              <li>
                <nj-connection
                  linkWarning_temporarilyUnavailable={this.linkWarning_temporarilyUnavailable}
                  linkWarning_noAvailability={this.linkWarning_noAvailability}
                  linkWarning_temporarilyInsufficientAvailability={this.linkWarning_temporarilyInsufficientAvailability}
                  mode={ReservationMode.Outward}
                  connection={connection}
                  fromToData={this.fromToData}
                  parentShadowRoot={this.parentShadowRoot}
                  enableNewInfobox={this.enableNewInfobox}
                  enableSearchagent={this.enableSearchagent}
                  simulateWarnings={this.simulateWarnings} />
              </li>
            ))}
          </ul>,
           this.loadOutwardDataMode==1?<p class="loader"><nj-loader size={NjLoaderSize.SMALL}></nj-loader> </p>:'',
          <p class="right">
            <button type="button" class="button--connections" onClick={() => this.loadOutwardData(1)}>
              <arrow-down-icon /> {i18n.getMessage("connections.later-connections")}
            </button>
          </p>,
        ];
      })(),
    ];
  }

  private renderReturn() {
    if (!this.fromToData || !this.fromToData.dateReturn) {
      return;
    }

    return [
      <h2 id="return-journey" class="return-journey">
        {i18n.getMessage("connections.return-journey")}
        {!this.showReturnForm ? (
          <button class="button--bordered" onClick={() => this.showReturnFormHandler()}>{i18n.getMessage("connections.edit-return-journey")}</button>
        ) : (
          <button onClick={() => this.hideReturnFormHandler()}>
            <span aria-hidden="true">&times;</span> {i18n.getMessage("connections.remove-return-journey")}
          </button>
        )}
      </h2>,

      this.renderWarningDate(this.returnDateWarning),

      <button type="button" class={{
        "previous": true,
        "button--connections": true,
        "previousReturnJourney": this.returnBackEnabled,
        "invisible":!this.returnBackEnabled
      }} onClick={() => this.loadReturnData(2)}><arrow-up-icon /> {i18n.getMessage("connections.previous-connections")}</button>,

      this.showReturnForm ? <nj-from-to mode="return" class="js-nj-from-to-return" submitLabel={i18n.getMessage('fromTo.searchConnectionsReturn', 'Rückfahrt suchen')} /> : null,

      (() => {
        if (!this.connectionDataReturn && !this.firstView) return this.renderLoader();

        if (this.connectionDataReturn && this.connectionDataReturn.length == 0) return <nj-validation type='ERROR'>{i18n.getMessage("connections.return-journey-no-connections")}</nj-validation>;

        return [
          this.loadReturnDataMode === 2 ? <p class="loader"><nj-loader size={NjLoaderSize.SMALL}></nj-loader> </p> : '',
          <ul>
            {this.connectionDataReturn?.map(connection => (
              <li>
                <nj-connection mode={ReservationMode.Return}
                               connection={connection}
                               fromToData={this.fromToReturnData ?? this.fromToData}
                               parentShadowRoot={this.parentShadowRoot}
                               linkWarning_temporarilyUnavailable={this.linkWarning_temporarilyUnavailable}
                               linkWarning_noAvailability={this.linkWarning_noAvailability}
                               linkWarning_temporarilyInsufficientAvailability={this.linkWarning_temporarilyInsufficientAvailability}
                               enableNewInfobox={this.enableNewInfobox}
                               enableSearchagent={this.enableSearchagent}
                />
              </li>
            ))}
          </ul>,
          this.loadReturnDataMode==1?<p class="loader"><nj-loader size={NjLoaderSize.SMALL}></nj-loader> </p>:'',
          <p class="right">
            <button class="button--connections" type="button" onClick={() => this.loadReturnData(1)}>
              <arrow-down-icon /> {i18n.getMessage("connections.later-connections")}
            </button>

          </p>,
        ];
      })(),
    ];
  }

  render() {
    return [
      this.renderWarningBikes(),
      <div class="relative js-nj-outward-journey">{this.renderOutward()}</div>,
      <div class="relative js-nj-return-journey">{this.renderReturn()}</div>,
    ];
  }
}
